import type { ChangeEvent } from 'react'
import { useCallback } from 'react'
import { SwitchBase } from '@instacart/ids-tooling'
import { Accordion, Text } from '@retailer-platform/shared-components'
import { useDashMessages } from '../../../../intl/intl.hooks'
import { useAiAgent } from '../../../../utils/contexts/ai/AiAgentContext'
import useAccessControl from '../../../../legacy/components/AccessControl/useAccessControl'
import { FeatureToggle } from '../../../../access-control/featureToggles'
import { Permission } from '../../../../access-control/permissions'

export const AiAgentEnabled = () => {
  let enabled: boolean
  let setEnabled: (enabled: boolean) => void
  let isAgentAvailable: boolean
  let isPageAiReady: boolean
  let isAgentPowered: boolean

  try {
    const aiAgent = useAiAgent()
    enabled = aiAgent.isAgentUserEnabled
    setEnabled = aiAgent.setEnabled
    isAgentAvailable = aiAgent.isAgentAvailable
    isPageAiReady = aiAgent.isPageAiReady
    isAgentPowered = aiAgent.isAgentPowered
  } catch (error) {
    // If AiAgent context is not available, default to enabled and no-op setter
    console.warn('AiAgent context not available in AiAgentEnabled control')
    enabled = true
    setEnabled = () => {}
    isAgentAvailable = false
    isPageAiReady = false
    isAgentPowered = false
  }

  // Check individual access control flags
  const hasAccess = useAccessControl()
  const hasFeatureToggle = hasAccess({
    featureToggles: FeatureToggle.IppAiAgent,
  })
  const hasPermission = hasAccess({
    permissions: [Permission.AiAgentExecute],
  })
  const hasSystemAccess = hasFeatureToggle && hasPermission

  const handleSwitchChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      setEnabled(event.target.checked)
    },
    [setEnabled]
  )

  const i18n = useDashMessages({
    aiAgentEnabledTitle: 'app.admin.controlPanel.setting.aiAgentEnabled.title',
    aiAgentEnabledDesc: 'app.admin.controlPanel.setting.aiAgentEnabled.description',
    aiAgentFlagStatus: 'app.admin.controlPanel.setting.aiAgentEnabled.flagStatus',
    aiAgentFeatureToggle: 'app.admin.controlPanel.setting.aiAgentEnabled.featureToggle',
    aiAgentPermission: 'app.admin.controlPanel.setting.aiAgentEnabled.permission',
    aiAgentSystemAccess: 'app.admin.controlPanel.setting.aiAgentEnabled.systemAccess',
    aiAgentPageReady: 'app.admin.controlPanel.setting.aiAgentEnabled.pageReady',
    aiAgentAvailable: 'app.admin.controlPanel.setting.aiAgentEnabled.available',
    aiAgentPowered: 'app.admin.controlPanel.setting.aiAgentEnabled.powered',
    aiAgentUserEnabled: 'app.admin.controlPanel.setting.aiAgentEnabled.userEnabled',
  })

  const title = (
    <div css={{ display: 'flex', flexDirection: 'row', alignItems: 'center' }}>
      {i18n.aiAgentEnabledTitle}

      <div css={{ flex: 1 }} />

      <div css={{ overflow: 'hidden' }}>
        <SwitchBase
          name="aiAgentEnabledSwitch"
          id="aiAgentEnabledSwitch"
          checked={enabled}
          onClick={evt => evt.stopPropagation()}
          onChange={handleSwitchChange}
        />
      </div>
    </div>
  )

  const FlagStatus = ({ label, value }: { label: string; value: boolean }) => (
    <div
      css={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        padding: '4px 0',
      }}
    >
      <Text size="small">{label}</Text>
      <Text
        size="small"
        css={{
          fontWeight: 'bold',
          color: value ? '#28a745' : '#dc3545',
        }}
      >
        {value ? '✓' : '✗'}
      </Text>
    </div>
  )

  return (
    <Accordion showContentBackground={true} title={title}>
      <div css={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
        <Text size="small">{i18n.aiAgentEnabledDesc}</Text>

        <div css={{ borderTop: '1px solid #e0e0e0', paddingTop: '12px' }}>
          <Text size="small" css={{ fontWeight: 'bold', marginBottom: '8px' }}>
            {i18n.aiAgentFlagStatus}
          </Text>

          <FlagStatus label={i18n.aiAgentUserEnabled} value={enabled} />
          <FlagStatus label={i18n.aiAgentFeatureToggle} value={hasFeatureToggle} />
          <FlagStatus label={i18n.aiAgentPermission} value={hasPermission} />
          <FlagStatus label={i18n.aiAgentSystemAccess} value={hasSystemAccess} />
          <FlagStatus label={i18n.aiAgentPageReady} value={isPageAiReady} />
          <FlagStatus label={i18n.aiAgentAvailable} value={isAgentAvailable} />
          <FlagStatus label={i18n.aiAgentPowered} value={isAgentPowered} />
        </div>
      </div>
    </Accordion>
  )
}
